import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
} from "@mui/material";
import { LogoBrand } from "assets/images";
import { useApp } from "context";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Enums, Helpers, Routes, Store } from "utils";
import { Custom, Modal, Form } from "components";
import { Auth } from "pages";
import routes from "utils/routes";
import QrCodeIcon from "@mui/icons-material/QrCode";
import TabletIcon from "@mui/icons-material/Tablet";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SyncIcon from "@mui/icons-material/Sync";

export const Navigation = (props: any) => {
  const navigate = useNavigate();
  const {
    t,
    theme,
    toggleTheme,
    user,
    setUser,
    service,
    feedback,
    setFeedback,
  } = useApp();

  const [syncDisabled, setSyncDisabled] = useState(false);

  const handleToggleTheme = () => {
    toggleTheme();
    document.documentElement.setAttribute(
      "theme",
      theme.id === Enums.EnumTheme.Light ? "dark" : "light"
    );
  };

  const handleResetPasswordModalOpen = async () => {
    setResetPasswordModal(true);
  };

  const handleResetPasswordModalClose = async () => {
    setResetPasswordModal(false);
    setFeedback(null);
  };

  const toggleWalkIn = async () => {
    const walkIn = !user.walkIn;
    service.clinic.update(walkIn);
    setUser({ walkIn });
  };

  const syncGoogleCalendar = async () => {
    setSyncDisabled(true);
    const [status, response] = await service.calendar.sync();
    setSyncDisabled(false);
  };

  const handleSignOut = async () => {
    await service.auth.signout(user.wq ?? "");
    // it should always clean the session no matter the status of
    // the response to prevent the user to be locked in the session.
    setUser(null);
    navigate(Routes.auth.signin);
  };

  const [profileModal, setProfileModal] = React.useState(false);
  const [resetPasswordModal, setResetPasswordModal] = React.useState(false);
  const handleProfileOpen = () => setProfileModal(true);
  const handleProfileClose = () => setProfileModal(false);

  const { pathname } = useLocation();

  useEffect(() => {
    isPatient &&
      pathname === routes.patient.home &&
      !Store.sessionStorage.get().completed &&
      setProfileModal(true);
  }, [pathname]);

  // const [qrcodeModal, setQRcodeModal] = React.useState(false);
  // const handleQRcodeOpen = () => setQRcodeModal(true);
  // const handleQRcodeClose = () => setQRcodeModal(false);

  // const [healthGoalModal, setHealthGoalModal] = React.useState(false);
  // const handleHealthGoalModalOpen = () => setHealthGoalModal(true);
  // const handleHealthGoalModalClose = () => setHealthGoalModal(false);

  // const [checkInModal, setCheckInModal] = React.useState(false);
  // const handleCheckInClose = () => setCheckInModal(false);

  const [menu, setMenu] = React.useState<null | HTMLElement>(null);
  const open = !!menu;
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setMenu(event.currentTarget);
  const handleMenuClose = () => setMenu(null);

  const isClinic = Store.sessionStorage.isClinic();
  const isPatient = Store.sessionStorage.isPatient();

  return (
    <Box sx={{ marginBottom: 8 }}>
      <AppBar
        position="fixed"
        sx={{
          color: theme.color.accent.textAccent,
          backgroundColor: theme.color.accent.color,
        }}
      >
        <Toolbar>
          <Grid container sx={{ justifyContent: "space-between" }}>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Link to={Routes.patient.home}>
                <LogoBrand style={{ width: "auto", height: 40 }} />
              </Link>
            </Grid>
            <Grid item>
              <Grid
                container
                sx={{ alignItems: "center", justifyContent: "flex-end" }}
              >
                <Box sx={{ display: "inline-flex", gap: theme.margin.xs }}>
                  <IconButton
                    size="medium"
                    color="inherit"
                    onClick={handleToggleTheme}
                  >
                    {theme.id === Enums.EnumTheme.Dark ? (
                      <DarkModeIcon />
                    ) : (
                      <LightModeIcon />
                    )}
                  </IconButton>
                  {/* {isPatient && (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={handleQRcodeOpen}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  )} */}
                  {isClinic && (
                    <>
                      <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => navigate(Routes.qiosk.checkin)}
                      >
                        <TabletIcon />
                      </IconButton>
                      {user.walkIn ? (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={toggleWalkIn}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={toggleWalkIn}
                        >
                          <CancelIcon />
                        </IconButton>
                      )}

                      <IconButton
                        size="small"
                        color="inherit"
                        onClick={syncGoogleCalendar}
                        disabled={syncDisabled}
                      >
                        <SyncIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    onClick={handleMenuOpen}
                    size="small"
                    aria-controls={open ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                  >
                    <Custom.Avatar name={user.username} />
                  </IconButton>
                  <Menu
                    open={open}
                    anchorEl={menu}
                    id="account-menu"
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        mt: theme.margin.sm,
                        color: theme.palette.font.color,
                        backgroundColor: theme.palette.background.color,
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    {isPatient && (
                      <MenuItem onClick={handleProfileOpen}>
                        {t.title.profile}
                      </MenuItem>
                    )}
                    {/* {isPatient && (
                      <MenuItem onClick={handleHealthGoalModalOpen}>
                        {t.title.patient.health_goal}
                      </MenuItem>
                    )} */}
                    <MenuItem onClick={handleResetPasswordModalOpen}>
                      {t.action.reset_password}
                    </MenuItem>
                    <MenuItem onClick={handleSignOut}>
                      {t.title.sign_out}
                    </MenuItem>
                  </Menu>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          {isPatient ? (
            <>
              <Modal.Profile
                opened={profileModal}
                onClose={handleProfileClose}
              />
              {/* <Modal.QRCode
                value={user.email}
                opened={qrcodeModal}
                onClose={handleQRcodeClose}
              /> */}
              {/* <Modal.HealthGoal
                opened={healthGoalModal}
                onClose={handleHealthGoalModalClose}
              /> */}
            </>
          ) : null}
          <Custom.Modal
            header={t.title.auth.reset_password}
            opened={resetPasswordModal}
            message={feedback?.message ?? ""}
            severity={feedback?.severity ?? undefined}
            onClose={handleResetPasswordModalClose}
          >
            <Form.ResetPasswordAfterLogin />
          </Custom.Modal>
        </Toolbar>
      </AppBar>
    </Box>
  );
};
